import React from 'react';
import { getTranslations } from '../utils/translations';

const AdCountdownUI = ({
  currentPlaylistItem,
  dimensions,
  adRemainingTime,
  adDuration,
  adSkipTimeOffset,
  onSkipAd,
  isAdMuted,
  onToggleMute,
  isSeries,
  isMobile,
}) => {
  const t = getTranslations();

  if (typeof adRemainingTime !== 'number') {
    return null;
  }

  const isSinglePlayer = currentPlaylistItem?.playerType === 'single';
  const canSkip = typeof adSkipTimeOffset === 'number' && adSkipTimeOffset <= 0;
  const skipCountdown = typeof adSkipTimeOffset === 'number' && adSkipTimeOffset > 0 ? Math.ceil(adSkipTimeOffset) : null;

  // Size & position the overlay to the video ad frame — identical to the IMA ad
  // container (adContainerRef) and AudioAdUI — so the controls sit on the video and
  // never bleed over the episode text below or the playlist to the right.
  const overlayStyle = {
    position: 'absolute',
    top: isSeries ? (isSinglePlayer ? 0 : 25) : 20,
    left: isSeries ? (isMobile ? 10 : (isSinglePlayer ? 0 : 25)) : 20,
    width: dimensions ? `${dimensions.width}px` : '100%',
    height: dimensions ? `${dimensions.height}px` : '100%',
    pointerEvents: 'none',
    zIndex: 101,
    fontFamily: 'Arial, sans-serif',
  };

  const timerStyle = {
    position: 'absolute',
    top: '10px',
    left: '10px',
    fontSize: '12px',
    fontWeight: '400',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '3px',
    margin: 0,
    pointerEvents: 'none',
  };

  const muteButtonStyle = {
    position: 'absolute',
    bottom: '10px',
    left: '10px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '6px 10px',
    borderRadius: '3px',
    margin: 0,
    border: '1px solid rgba(255,255,255,0.6)',
    cursor: 'pointer',
    pointerEvents: 'auto',
  };

  const advertisementTextStyle = {
    position: 'absolute',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '12px',
    letterSpacing: '1px',
    fontWeight: '400',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '5px 15px',
    borderRadius: '3px',
    margin: 0,
    pointerEvents: 'none',
    textTransform: 'uppercase',
  };

  const skipButtonStyle = {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '3px',
    margin: 0,
    border: '1px solid rgba(255,255,255,0.6)',
    cursor: canSkip ? 'pointer' : 'default',
    pointerEvents: canSkip ? 'auto' : 'none',
    display: (canSkip || skipCountdown !== null) ? 'block' : 'none',
  };

  return (
    <div id="ad-countdown-overlay" style={overlayStyle}>
      <div style={timerStyle}>
        {t.ad}{Math.ceil(adRemainingTime)}s
      </div>

      {typeof onToggleMute === 'function' && (
        <div
          role="button"
          aria-pressed={!isAdMuted}
          tabIndex={0}
          style={muteButtonStyle}
          onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onToggleMute();
            }
          }}
          className="video-ad-mute"
        >
          {isAdMuted ? t.unmute : t.mute}
        </div>
      )}

      {(canSkip || skipCountdown !== null) && (
        <div
          role="button"
          tabIndex={canSkip ? 0 : -1}
          style={skipButtonStyle}
          onClick={canSkip ? (e) => { e.stopPropagation(); onSkipAd(); } : undefined}
          onKeyDown={(e) => {
            if (canSkip && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              e.stopPropagation();
              onSkipAd();
            }
          }}
          className="video-ad-skip"
        >
          {canSkip ? t.skipAd : `${t.skipAdIn}${skipCountdown}s`}
        </div>
      )}

      <div style={advertisementTextStyle}>
        {t.advertisement}
      </div>
    </div>
  );
};

export default AdCountdownUI;
